import React, { useState } from 'react';
import styled from 'styled-components';
import { toast } from 'react-hot-toast';
import { ChevronDown, ChevronUp, History, Trash2, Calendar, MapPin, Users, Scale, AlertCircle, Edit3, User } from 'lucide-react';
import { CommitteeDto, CommitteeDecisionStatus, committeesApi, handleApiError } from '../../services/committeesService';
import AssigneesControl from '../common/AssigneesControl';
import { formatAllAssigneeNames, formatAssigneeNames } from '../../utils/assignees';
import { hexToRgba } from '../../utils/colorUtils';

interface CommitteeHistoryProps {
  committees: CommitteeDto[];
  onDeleteCommittee?: (id: number) => void;
  onUpdateCommittee?: (committee: CommitteeDto) => void;
  onReassigned?: () => void;
  loading?: boolean;
}

const HistoryContainer = styled.div`
  direction: rtl;
`;

const HistoryHeader = styled.button<{ $isExpanded: boolean }>`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px;
  background: var(--background-secondary);
  border: none;
  border-radius: var(--border-radius-md);
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: var(--hover-background);
  }
`;

const HeaderTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 16px;
  font-weight: 600;
  color: var(--text-primary);
`;

const HeaderIcon = styled.span`
  display: flex;
  align-items: center;
  color: var(--text-secondary);
`;

const CountBadge = styled.span`
  color: var(--text-secondary);
  font-size: 14px;
  font-weight: 500;
`;

const ExpandIcon = styled.span<{ $isExpanded?: boolean }>`
  display: flex;
  align-items: center;
  color: ${props => props.$isExpanded ? 'var(--primary-color)' : 'var(--text-secondary)'};
  transition: all 0.2s ease;
`;

const HistoryContent = styled.div<{ $isExpanded: boolean }>`
  max-height: ${props => props.$isExpanded ? '2000px' : '0'};
  overflow: hidden;
  transition: max-height 0.3s ease;
`;

const CommitteesList = styled.div`
  padding: 16px 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const CommitteeCard = styled.div`
  background: var(--surface-color);
  border: 1px solid var(--border-color);
  border-right: 4px solid var(--primary-color);
  border-radius: var(--border-radius-md);
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
`;

const CardHeader = styled.div<{ $isExpanded: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px;
  cursor: pointer;
  transition: background 0.2s ease;

  &:hover {
    background: var(--surface-color);
  }
`;

const CardHeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const CardHeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const EvaluationBadge = styled.span`
  background: var(--background-secondary);
  color: var(--text-secondary);
  padding: 4px 10px;
  border-radius: var(--border-radius-sm);
  font-size: 12px;
  font-weight: 600;
`;

const CommitteeDate = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: var(--text-primary);
`;

const StatusBadge = styled.span<{ $color: string }>`
  padding: 4px 10px;
  border-radius: var(--border-radius-sm);
  font-size: 12px;
  font-weight: 600;
  background: ${props => hexToRgba(props.$color, 0.12)};
  color: ${props => props.$color};
`;

const PercentageBadge = styled.span`
  font-size: 14px;
  font-weight: 700;
  color: #16a34a;
`;

const AssigneeChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  color: var(--text-secondary);
`;

const AssigneeRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid var(--border-color);
  flex-wrap: wrap;
`;

const ActionButton = styled.button<{ $variant?: 'edit' | 'delete' }>`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 6px 10px;
  background: transparent;
  border: none;
  color: var(--text-tertiary);
  cursor: pointer;
  border-radius: var(--border-radius-sm);
  transition: all 0.2s ease;
  font-size: 12px;
  font-weight: 500;

  ${props => props.$variant === 'edit' ? `
    &:hover {
      background: var(--primary-light);
      color: var(--primary-color);
    }
  ` : `
    &:hover {
      background: rgba(220, 38, 38, 0.1);
      color: #dc2626;
    }
  `}
`;

const DeleteButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 6px;
  background: transparent;
  border: none;
  color: var(--text-tertiary);
  cursor: pointer;
  border-radius: var(--border-radius-sm);
  transition: all 0.2s ease;

  &:hover {
    background: rgba(220, 38, 38, 0.1);
    color: #dc2626;
  }
`;

const CardContent = styled.div<{ $isExpanded: boolean }>`
  max-height: ${props => props.$isExpanded ? '500px' : '0'};
  overflow: hidden;
  transition: max-height 0.3s ease;
`;

const CardContentInner = styled.div`
  padding: 16px;
  border-top: 1px solid var(--border-color);
  background: var(--surface-color);
`;

const DetailsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 12px;
`;

const DetailItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const DetailLabel = styled.span`
  font-size: 11px;
  font-weight: 500;
  color: var(--text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const DetailValue = styled.span`
  font-size: 13px;
  font-weight: 500;
  color: var(--text-primary);
`;

const NotesSection = styled.div`
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid var(--border-color);
`;

const NotesLabel = styled.span`
  font-size: 11px;
  font-weight: 500;
  color: var(--text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  display: block;
  margin-bottom: 4px;
`;

const NotesText = styled.p`
  margin: 0;
  font-size: 13px;
  color: var(--text-primary);
  white-space: pre-wrap;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 40px 20px;
  color: var(--text-secondary);
`;

const EmptyIcon = styled.div`
  margin-bottom: 12px;
  opacity: 0.5;
`;

const EmptyText = styled.p`
  margin: 0;
  font-size: 14px;
`;

const LoadingState = styled.div`
  text-align: center;
  padding: 30px;
  color: var(--text-secondary);
  font-size: 14px;
`;

const ConfirmDialog = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const DialogContent = styled.div`
  background: white;
  padding: 24px;
  border-radius: var(--border-radius-lg);
  max-width: 400px;
  width: 90%;
  direction: rtl;
`;

const DialogTitle = styled.h3`
  margin: 0 0 12px 0;
  font-size: 18px;
  font-weight: 600;
  color: var(--text-primary);
`;

const DialogText = styled.p`
  margin: 0 0 20px 0;
  font-size: 14px;
  color: var(--text-secondary);
`;

const DialogButtons = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
`;

const DialogButton = styled.button<{ $variant?: 'danger' | 'secondary' }>`
  padding: 10px 20px;
  border: none;
  border-radius: var(--border-radius-md);
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  ${props => props.$variant === 'danger' ? `
    background: #dc2626;
    color: white;
    &:hover { opacity: 0.9; }
  ` : `
    background: var(--background-secondary);
    color: var(--text-primary);
    &:hover { background: var(--border-color); }
  `}
`;

const formatDate = (dateString?: string): string => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return date.toLocaleDateString('he-IL', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
};

const formatDateTime = (dateString?: string): string => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return date.toLocaleDateString('he-IL', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const formatCurrency = (amount?: number): string => {
  if (!amount) return '-';
  return `₪${amount.toLocaleString('he-IL')}`;
};

const getDecisionColor = (status: CommitteeDecisionStatus): string => {
  switch (status) {
    case CommitteeDecisionStatus.ApprovedPermanent:
    case CommitteeDecisionStatus.ApprovedForPeriod:
      return '#16a34a';
    case CommitteeDecisionStatus.Rejected:
      return '#dc2626';
    default:
      return '#d97706';
  }
};

const CommitteeHistory: React.FC<CommitteeHistoryProps> = ({
  committees,
  onDeleteCommittee,
  onUpdateCommittee,
  onReassigned,
  loading
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [expandedCards, setExpandedCards] = useState<Set<number>>(new Set());
  const [deleteConfirm, setDeleteConfirm] = useState<number | null>(null);

  const handleSetAssignees = async (committeeId: number, userIds: number[]) => {
    try {
      await committeesApi.setAssignees(committeeId, userIds);
      toast.success('המשויכים עודכנו בהצלחה');
      onReassigned?.();
    } catch (error) {
      toast.error(handleApiError(error, 'שגיאה בעדכון המשויכים'));
      throw error;
    }
  };

  const toggleCard = (id: number) => {
    const newExpanded = new Set(expandedCards);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedCards(newExpanded);
  };

  const handleDelete = (id: number, e: React.MouseEvent) => {
    e.stopPropagation();
    setDeleteConfirm(id);
  };

  const handleUpdate = (committee: CommitteeDto, e: React.MouseEvent) => {
    e.stopPropagation();
    onUpdateCommittee?.(committee);
  };

  const confirmDelete = () => {
    if (deleteConfirm !== null && onDeleteCommittee) {
      onDeleteCommittee(deleteConfirm);
      setDeleteConfirm(null);
    }
  };

  if (loading) {
    return (
      <HistoryContainer>
        <HistoryHeader $isExpanded={false} onClick={() => {}}>
          <HeaderTitle>
            <HeaderIcon><History size={18} /></HeaderIcon>
            היסטוריית וועדות
          </HeaderTitle>
        </HistoryHeader>
        <HistoryContent $isExpanded={true}>
          <LoadingState>טוען היסטוריה...</LoadingState>
        </HistoryContent>
      </HistoryContainer>
    );
  }

  return (
    <>
      <HistoryContainer>
        <HistoryHeader $isExpanded={isExpanded} onClick={() => setIsExpanded(!isExpanded)}>
          <HeaderTitle>
            <HeaderIcon><History size={18} /></HeaderIcon>
            היסטוריית וועדות
            <CountBadge>({committees.length})</CountBadge>
          </HeaderTitle>
          <ExpandIcon $isExpanded={isExpanded}>
            {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
          </ExpandIcon>
        </HistoryHeader>

        <HistoryContent $isExpanded={isExpanded}>
          {committees.length === 0 ? (
            <CommitteesList>
              <EmptyState>
                <EmptyIcon><AlertCircle size={40} /></EmptyIcon>
                <EmptyText>אין וועדות קודמות</EmptyText>
              </EmptyState>
            </CommitteesList>
          ) : (
            <CommitteesList>
              {committees.map(committee => {
                const isCardExpanded = expandedCards.has(committee.id);
                const statusColor = getDecisionColor(committee.decisionStatus);

                return (
                  <CommitteeCard key={committee.id}>
                    <CardHeader
                      $isExpanded={isCardExpanded}
                      onClick={() => toggleCard(committee.id)}
                    >
                      <CardHeaderLeft>
                        <EvaluationBadge>הערכה {committee.evaluationNumber}</EvaluationBadge>
                        <CommitteeDate>{formatDateTime(committee.committeeDate)}</CommitteeDate>
                        <StatusBadge $color={statusColor}>
                          {committee.decisionStatusName}
                        </StatusBadge>
                        {committee.disabilityPercentage && (
                          <PercentageBadge>{committee.disabilityPercentage}%</PercentageBadge>
                        )}
                        <AssigneeChip title={formatAllAssigneeNames(committee.assignees)}>
                          {committee.assignees.length > 1
                            ? <Users size={12} strokeWidth={2} />
                            : <User size={12} strokeWidth={2} />}
                          {formatAssigneeNames(committee.assignees)}
                        </AssigneeChip>
                      </CardHeaderLeft>
                      <CardHeaderRight>
                        {onUpdateCommittee && (
                          <ActionButton
                            $variant="edit"
                            onClick={(e) => handleUpdate(committee, e)}
                            title={committee.decisionStatus === CommitteeDecisionStatus.Pending ? 'הזנת החלטה' : 'עריכת החלטה'}
                          >
                            <Edit3 size={14} />
                            {committee.decisionStatus === CommitteeDecisionStatus.Pending ? 'הזנת החלטה' : 'עריכת החלטה'}
                          </ActionButton>
                        )}
                        {onDeleteCommittee && (
                          <DeleteButton onClick={(e) => handleDelete(committee.id, e)}>
                            <Trash2 size={16} />
                          </DeleteButton>
                        )}
                        <ExpandIcon>
                          {isCardExpanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
                        </ExpandIcon>
                      </CardHeaderRight>
                    </CardHeader>

                    <CardContent $isExpanded={isCardExpanded}>
                      <CardContentInner>
                        <DetailsGrid>
                          {committee.location && (
                            <DetailItem>
                              <DetailLabel>
                                <MapPin size={12} style={{ marginLeft: 4 }} />
                                מיקום
                              </DetailLabel>
                              <DetailValue>{committee.location}</DetailValue>
                            </DetailItem>
                          )}
                          <DetailItem>
                            <DetailLabel>
                              <Users size={12} style={{ marginLeft: 4 }} />
                              ליווי
                            </DetailLabel>
                            <DetailValue>
                              {committee.escortNeeded
                                ? `כן - ${committee.escortType || 'לא צוין'}`
                                : 'לא'}
                            </DetailValue>
                          </DetailItem>
                          {committee.decisionStatus === CommitteeDecisionStatus.ApprovedForPeriod &&
                           committee.approvalEndDate && (
                            <DetailItem>
                              <DetailLabel>
                                <Calendar size={12} style={{ marginLeft: 4 }} />
                                תאריך סיום
                              </DetailLabel>
                              <DetailValue>{formatDate(committee.approvalEndDate)}</DetailValue>
                            </DetailItem>
                          )}
                          {!!committee.monthlyBenefitAmount && (
                            <DetailItem>
                              <DetailLabel>
                                <Scale size={12} style={{ marginLeft: 4 }} />
                                גמלה חודשית
                              </DetailLabel>
                              <DetailValue>{formatCurrency(committee.monthlyBenefitAmount)}</DetailValue>
                            </DetailItem>
                          )}
                          {committee.retroactiveTotalAmount && (
                            <DetailItem>
                              <DetailLabel>סכום רטרואקטיבי</DetailLabel>
                              <DetailValue>{formatCurrency(committee.retroactiveTotalAmount)}</DetailValue>
                            </DetailItem>
                          )}
                          <DetailItem>
                            <DetailLabel>נוצר על ידי</DetailLabel>
                            <DetailValue>{committee.createdByName}</DetailValue>
                          </DetailItem>
                        </DetailsGrid>

                        <AssigneeRow>
                          <DetailLabel>עובדים משויכים</DetailLabel>
                          <AssigneesControl
                            assignees={committee.assignees}
                            onSave={(userIds) => handleSetAssignees(committee.id, userIds)}
                          />
                        </AssigneeRow>

                        {(committee.notes || committee.decisionNotes) && (
                          <NotesSection>
                            {committee.notes && (
                              <>
                                <NotesLabel>הערות</NotesLabel>
                                <NotesText>{committee.notes}</NotesText>
                              </>
                            )}
                            {committee.decisionNotes && (
                              <>
                                <NotesLabel style={{ marginTop: committee.notes ? 12 : 0 }}>
                                  הערות החלטה
                                </NotesLabel>
                                <NotesText>{committee.decisionNotes}</NotesText>
                              </>
                            )}
                          </NotesSection>
                        )}
                      </CardContentInner>
                    </CardContent>
                  </CommitteeCard>
                );
              })}
            </CommitteesList>
          )}
        </HistoryContent>
      </HistoryContainer>

      {/* Delete Confirmation Dialog */}
      {deleteConfirm !== null && (
        <ConfirmDialog onClick={() => setDeleteConfirm(null)}>
          <DialogContent onClick={e => e.stopPropagation()}>
            <DialogTitle>מחיקת וועדה</DialogTitle>
            <DialogText>
              האם אתה בטוח שברצונך למחוק את הוועדה?
              פעולה זו אינה ניתנת לביטול.
            </DialogText>
            <DialogButtons>
              <DialogButton onClick={() => setDeleteConfirm(null)}>
                ביטול
              </DialogButton>
              <DialogButton $variant="danger" onClick={confirmDelete}>
                מחק
              </DialogButton>
            </DialogButtons>
          </DialogContent>
        </ConfirmDialog>
      )}
    </>
  );
};

export default CommitteeHistory;
